/* eslint-disable no-unused-vars */
// library imports
import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import axios from "axios";
import { useTheme, makeStyles } from "@mui/styles";
import {
  Checkbox,
  Grid,
  Box,
  Container,
  ListItemText,
  Link,
  MenuItem,
  Typography,
  useMediaQuery,
  CardMedia,
  Card,
} from "@mui/material";
import StarIcon from "@mui/icons-material/Star";
import SentimentDissatisfiedIcon from "@mui/icons-material/SentimentDissatisfied";
import InfiniteScroll from "react-infinite-scroll-component";
import { useLocation, useNavigate } from "react-router-dom";
import "react-toggle/style.css";

// file imports
import ShowFilmsBtn from "./ShowFilmsBtn";
import AdultToggle from "./AdultToggle";
import RatingSlider from "./RatingSlider";
import YearSlider from "./YearSlider";
import VoteSlider from "./VoteSlider";
import { niceVoteStep, getGroupVoteMax, rescaleVotes } from "./voteScale";
import DebouncedSelect from "./DebouncedSelect";
import MultiSelectType from "./MultiSelectType";
import SortSelect from "./SortSelect";
import LanguageSelect from "./LanguageSelect";
import { CircleLoader } from "./CircleLoader";
import { Circles } from "react-loader-spinner";
import AuthContext from "../context/AuthContext";

const currentYear = new Date().getFullYear();
const roles = {
  default: {
    genre: ["Action", "Adventure"],
    year: [currentYear - 3, currentYear],
    votes: 50000,
    titleType: ["Movies"],
    isAdult: false,
  },
  gamer: {
    genre: ["Fantasy", "Sci-Fi"],
    year: [2005, 2020],
    titleType: ["videoGame"],
  },
  oldies: {
    genre: ["Drama", "Romance"],
    year: [1945, 1965],
    titleType: ["movie", "tvMovie"],
    isAdult: false,
  },
};

const gridCardStyles = makeStyles((theme) => ({
  filterContainer: {
    height: "280px",
    [theme.breakpoints.down("955")]: {
      height: "320px",
    },
  },
  cardroot: {
    "& .details": {
      display: "none",
    },
    [theme.breakpoints.up("md")]: {
      "&:hover .details": {
        display: "flex",
      },
    },
    [theme.breakpoints.down("sm")]: {
      "& .details": {
        display: "flex",
      },
    },
    "&:hover .poster": {
      filter: "blur(2px)",
    },
  },
  scrollable: {
    overflowY: "visible",
    paddingTop: "0px",
    marginTop: "0px",
  },
}));

const pkgBaseUrl = process.env.REACT_APP_API_BASE_URL;
axios.defaults.baseURL = pkgBaseUrl;
const apiver = "api/v3";
const apiBaseUrl = `${pkgBaseUrl}/${apiver}`;
const basicStartYear = 1895;
const YEAR_SNAP_PADDING = 5;
const defaultRoleYear = 1990;
const yearValues = [defaultRoleYear, currentYear];

// Json Data
const genreList = [
  "Action",
  "Adult",
  "Adventure",
  "Animation",
  "Biography",
  "Comedy",
  "Crime",
  "Documentary",
  "Drama",
  "Family",
  "Fantasy",
  "Film-Noir",
  "Game-Show",
  "History",
  "Horror",
  "Music",
  "Musical",
  "Mystery",
  "News",
  "Reality-TV",
  "Romance",
  "Sci-Fi",
  "Short",
  "Sport",
  "Talk-Show",
  "Thriller",
  "War",
  "Western",
];


const autotypes = [
  { name: "Title", value: "title" },
  { name: "Actor", value: "name" },
];

const genres = genreList.map((m) => ({ name: m, value: m }));

const sortOptions = [
  { name: "Rating", value: "rating" },
  { name: "Title", value: "title" },
  { name: "Votes", value: "votes" },
  { name: "Year", value: "year" },
];

const typeGrouping = {
  Movies: ["movie"],
  Shorts: ["short"],
  "TV Shows": ["tvSeries", "tvMiniSeries", "tvEpisode", "tvMovie", "tvSpecial", "tvShort"],
  "Video Games": ["videoGame"],
  "Other Videos": ["video"],
};

// Show-button noun per movie-type group; mixed/empty selection falls back to "Titles".
const showButtonNouns = {
  Movies: "Films",
  Shorts: "Shorts",
  "TV Shows": "Series",
  "Video Games": "Games",
  "Other Videos": "Videos",
};

const getShowButtonLabel = (selectedGroups = []) =>
  selectedGroups.length === 1
    ? `Show ${showButtonNouns[selectedGroups[0]] ?? "Titles"}`
    : "Show Titles";

// Per-titleType max vote counts. Static fallback (sampled from the titles
// collection); replaced at runtime by GET /api/v3/ranges when available so the
// votes slider scales to each media type instead of a fixed 0..2M range.
const STATIC_VOTE_MAX_BY_TYPE = {
  movie: 3193411,
  tvSeries: 2624707,
  tvMiniSeries: 1041078,
  tvEpisode: 500988,
  tvSpecial: 109172,
  tvMovie: 105541,
  video: 91158,
  videoGame: 90866,
  short: 88869,
  tvShort: 25114,
  tvPilot: 0,
};

// Module-level cache: fetch per-titleType vote ceilings once per tab session
// (the collection is regenerated daily by an out-of-band ETL, so it's safe to
// reuse). Returns a {titleType: maxRatingVotes} map, or null on failure (which
// clears the cache so a later mount can retry). `?type=ratingVotes` trims the
// projection to just the votes min/max.
let voteRangesPromise = null;
const fetchVoteMaxByType = () => {
  if (!voteRangesPromise) {
    voteRangesPromise = axios
      .get(`${apiver}/ranges?type=ratingVotes`)
      .then((res) => {
        if (!Array.isArray(res.data)) return null;
        const next = {};
        res.data.forEach((d) => {
          if (d && d._id != null) next[d._id] = d.maxRatingVotes ?? 0;
        });
        return Object.keys(next).length ? next : null;
      })
      .catch(() => {
        voteRangesPromise = null; // allow retry on a later mount
        return null;
      });
  }
  return voteRangesPromise;
};

const mapTitleTypesToGroups = (types = []) => {
  const selectedGroups = Object.entries(typeGrouping).reduce(
    (acc, [group, values]) => {
      if (types.some((type) => values.includes(type))) {
        acc.push(group);
      }
      return acc;
    },
    []
  );
  return selectedGroups;
};

const movieTypeOptions = [
  { name: "Movies", value: "Movies" },
  { name: "Shorts", value: "Shorts" },
  { name: "TV Shows", value: "TV Shows" },
  { name: "Video Games", value: "Video Games" },
  { name: "Other Videos", value: "Other Videos" },
];

// Utility Functions
const getWindowWidth = () => {
  return window.innerWidth;
};

const getWindowHeight = () => {
  return window.innerHeight;
};

const valuetext = (value) => {
  return `${value}`;
};

const escapeRegexCharacters = (str) => {
  return str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
};

const capitalize = (str, lowerRest = false) => {
  const capitalizeWord = (word, shouldCapitalize) =>
    shouldCapitalize
      ? word.charAt(0).toUpperCase() +
        (lowerRest ? word.slice(1).toLowerCase() : word.slice(1))
      : lowerRest
      ? word.toLowerCase()
      : word;

  const words = str.split(" ");

  const capitalizedString = words
    .map((word, index) =>
      // Capitalize the first and last words, others based on lowerRest
      capitalizeWord(word, index === 0 || index === words.length - 1)
    )
    .join(" ");

  //console.log("got back " + capitalizedString);
  return capitalizedString;
};

const renameKey = (origKey, newKey, objList) => {
  const newList = objList.map((obj) => {
    obj[newKey] = obj[origKey];
    delete obj[origKey];
    return obj;
  });
  return newList;
};

const addUrl = (objList) => {
  const newList = objList.map((obj) => {
    const newUrl = `${apiBaseUrl}/image/${obj.id}`;
    return { ...obj, url: newUrl };
  });
  return newList;
};

const getData = (path, outerData, headers = {}) => {
  //  console.log("getData with query " + outerData.genre)
  return axios.post(`${apiver + path}`, JSON.stringify(outerData), { headers });
};

const toCommaSeparated = (value) => {
  return Array.isArray(value) ? value.join(",") : value;
};

const Search = ({
  imageDimensions,
  showSearch,
  setShowSearch,
  posters,
  setPosters,
  initialLoadRef, // Consider removing this prop if not necessary
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const classes = gridCardStyles({ isMobile, isTablet });
  const navigate = useNavigate();
  const location = useLocation();
  const { tokenPair, guestLogin } = useContext(AuthContext);

  // All states
  const [autotype, setAutotype] = useState("title");
  const [rating, setRating] = useState("7");
  const [votes, setVotes] = useState("50000");
  const [voteMaxByType, setVoteMaxByType] = useState(STATIC_VOTE_MAX_BY_TYPE);
  const [nameSuggestions, setNameSuggestions] = useState([]);
  const [titleSuggestions, setTitleSuggestions] = useState([]);
  const [selectedGenres, setSelectedGenres] = useState(["Action", "Adventure"]);
  const [textValue, setTextValue] = useState("");
  const [selectedTitleType, setSelectedTitleType] = useState(["movie"]);
  const [adult, setAdult] = useState(false);
  const [language, setLanguage] = useState("en");
  const [years, setYears] = useState(yearValues);
  const [page, setPage] = useState(1);
  const [showDisabled, setShowDisabled] = useState(false);
  const [hasMoreData, setHasMoreData] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [lastTitleSelection, setLastTitleSelection] = useState({
    primaryTitle: "",
    isModified: false,
    selected: false,
    fromSuggestion: false,
  });
  const [lastNameSelection, setLastNameSelection] = useState({
    id: "",
    primaryName: "",
    baseName: "",
    birthYear: null,
    deathYear: null,
    selected: false,
    fromSuggestion: false,
  });
  const [lastQuery, setLastQuery] = useState({
    ...roles["default"],
    titleType: roles["default"].titleType.flatMap((group) => typeGrouping[group]),
    sortType: "rating",
    lang: "en",
  });
  const [isAppending, setIsAppending] = useState(false);
  const [width, setWidth] = useState(document.documentElement.clientWidth);
  const [searching, setSearching] = useState(false);
  const [selectedTypeGroups, setSelectedTypeGroups] = useState(roles['default'].titleType);
  const [sortType, setSortType] = useState("rating");

  const hasInputText = autotype === "title"
    ? !!lastTitleSelection.primaryTitle
    : !!textValue;

  const filtersDisabled = hasInputText && !(
    autotype === "title" ? lastTitleSelection.fromSuggestion : lastNameSelection.selected
  );

  const onSnapYear = (newYears) => {
    setYears(newYears);
    setLastQuery((prev) => ({ ...prev, year: newYears }));
  };

  // Vote slider bounds scale to the selected media type(s).
  const voteMax = getGroupVoteMax(selectedTypeGroups, voteMaxByType, typeGrouping);
  const voteStep = niceVoteStep(voteMax);

  // All refs
  const lastQueryRef = useRef(lastQuery);
  const lastTitleSelectionRef = useRef(lastTitleSelection);
  const hasMoreDataRef = useRef(hasMoreData);
  const isMountedRef = useRef(true);
  const isAppendingRef = useRef(isAppending);
  // Local ref to prevent multiple initial fetches
  const hasFetchedInitialData = useRef(false);

  const updateTitleSelection = useCallback((value) => {
    const resolved = typeof value === "function" ? value(lastTitleSelectionRef.current) : value;
    lastTitleSelectionRef.current = resolved;
    setLastTitleSelection(resolved);
  }, []);

  // For building the API path while infinite scrolling
const makePath = (pg, rtng, cardObj, baseTypeOverride) => {
  const windowWidth = getWindowWidth();
  const windowHeight = getWindowHeight();
  const cardWidth = cardObj.width;
  const cardHeight = cardObj.height;
  const baseTypeValue =
    baseTypeOverride === "name" ? "name" : baseTypeOverride || autotype;
  const baseType = baseTypeValue === "name" ? "name" : baseTypeValue;
  return (
    "/" +
    baseType +
      "/" +
      pg +
      "/" +
      rtng +
      "?ws=" +
      windowWidth +
      "&wh=" +
      windowHeight +
      "&cs=" +
      cardWidth +
      "&ch=" +
      cardHeight +
      "&off=0"
    );
  };

  /* All Handler functions */

  // For Processing the API response
  const handleResponse = (res) => {
    const remainingCount = parseInt(res.headers["x-remaining-count"], 10);
//    console.log(`Response received. Data length: ${res.data.length}, Remaining Count: ${remainingCount}`);
    
    if (res.data.length === 0 && remainingCount === 0) {
      setHasMoreData(false);
//      console.log("No more data. Setting hasMoreData to false.");
      if (page === 1) setPosters([]);
    } else if (res.data.length !== 0 && remainingCount === 0) {
      processData(res.data);
      setHasMoreData(false);
//      console.log("Fetched data but no remaining count. Setting hasMoreData to false.");
    } else {
      processData(res.data);
//      console.log("Fetched data and there is remaining count. hasMoreData remains true.");
    }
  };
  

  // Note: Depending on browser...   setWidth(window.innerWidth)
  const handleResize = () => setWidth(document.documentElement.clientWidth);

  const handleShowFilmsClick = (e) => {
    setShowSearch(false);
    onSubmit(e);
  };

  const applyHistorySearch = (historyParams) => {
    if (!historyParams) return;

    const nextYears = Array.isArray(historyParams.year)
      ? historyParams.year
      : years;
    const nextVotes = historyParams.votes ?? votes;
    const nextGenres =
      historyParams.genre && historyParams.genre.length
        ? historyParams.genre
        : [];
    const nextSort = historyParams.sortType ?? sortType;
    const nextLang = historyParams.lang ?? language;
    const nextAdult = historyParams.isAdult ?? false;
    const nextTitleTypes =
      historyParams.titleType && historyParams.titleType.length
        ? historyParams.titleType
        : lastQuery.titleType;
    const nextTypeGroups = mapTitleTypesToGroups(nextTitleTypes);
    const nextRating = historyParams.rating ?? rating;
    const nextQueryValue = historyParams.query ?? "";
    const derivedAutotype =
      nextQueryValue && /^nm\d+/i.test(nextQueryValue) ? "name" : "title";

    setAutotype(derivedAutotype);
    setShowDisabled(derivedAutotype === "name");
    setRating(String(nextRating));
    setYears(nextYears);
    setVotes(nextVotes);
    setSelectedGenres(nextGenres);
    setSortType(nextSort);
    setLanguage(nextLang);
    setAdult(nextAdult);
    setSelectedTypeGroups(
      nextTypeGroups.length ? nextTypeGroups : roles["default"].titleType
    );

    if (derivedAutotype === "title") {
      updateTitleSelection({
        primaryTitle: nextQueryValue,
        isModified: false,
        selected: !!nextQueryValue,
        fromSuggestion: false,
      });
      setLastNameSelection({
        id: "",
        primaryName: "",
        baseName: "",
        birthYear: null,
        deathYear: null,
        selected: false,
        fromSuggestion: false,
      });
      setTextValue(nextQueryValue);
    } else {
      updateTitleSelection({
        primaryTitle: "",
        isModified: false,
        selected: false,
        fromSuggestion: false,
      });
      setLastNameSelection({
        id: nextQueryValue,
        primaryName: nextQueryValue,
        baseName: nextQueryValue,
        birthYear: null,
        deathYear: null,
        selected: !!nextQueryValue,
        fromSuggestion: false,
      });
      setTextValue(nextQueryValue);
    }

    const cleanedQuery = {
      year: nextYears,
      votes: nextVotes,
      titleType: nextTitleTypes,
      isAdult: nextAdult,
      sortType: nextSort,
      lang: nextLang,
      genre:
        historyParams && Object.prototype.hasOwnProperty.call(historyParams, "genre")
          ? historyParams.genre ?? []
          : [],
      ...(nextQueryValue ? { query: nextQueryValue } : {}),
    };

    setHasMoreData(true);
    hasFetchedInitialData.current = true;
    setPosters([]);
    setPage(1);
    setSearching(true);
    setLastQuery(cleanedQuery);
    lastQueryRef.current = cleanedQuery;
    setShowSearch(false);

    setTimeout(() => {
      onSubmit({ preventDefault: () => {} }, cleanedQuery, derivedAutotype);
    }, 0);
  };

  const triggerDataFetch = () => {
    if (!isLoading && hasMoreData) {
      setPage((prevPage) => prevPage + 1);
//      console.log(`Triggering data fetch for page ${page + 1}`);
//    } else {
//      console.log(`Cannot fetch data. isLoading: ${isLoading}, hasMoreData: ${hasMoreData}`);
    }
  };
  

  // Fetching data for the page Using APIs
  const fetchDataForPage = async (page, isFirstPage = false, baseTypeOverride) => {
    let accessToken = tokenPair?.access;
    if (!accessToken) {
      try {
        const data = await guestLogin();
        accessToken = data.access;
      } catch (e) {
        navigate("/login");
        return;
      }
    }
    if (isLoading) return;

    setIsLoading(true);
    setIsAppending(!isFirstPage);
    isAppendingRef.current = !isFirstPage
//    console.log(`Fetching data for page ${page}, isFirstPage: ${isFirstPage}, isAppending: ${!isFirstPage}`);

    let clearTitleAfterFetch = false;
    let clearNameSuggestionAfterFetch = false;

    try {
      const prevRating = rating;

      const path = makePath(page, prevRating, imageDimensions, baseTypeOverride);
      const dataQuery = lastQueryRef.current;
      const stQuery = (() => {
        const effectiveAutotype = baseTypeOverride || autotype;
        const { query, searchType, year: persistedYear, ...rest } = dataQuery;
        const baseFilters = persistedYear
          ? { ...rest, year: persistedYear }
          : { ...rest };

        const getNameYears = () => {
          if (!lastNameSelection.selected) {
            return persistedYear;
          }

          const derivedYears = buildYearArrayFromSelection({
            birthYear: lastNameSelection.birthYear,
            deathYear: lastNameSelection.deathYear,
          });

          return derivedYears.length > 0 ? derivedYears : undefined;
        };

        if (effectiveAutotype === "title") {
          const titleSel = lastTitleSelectionRef.current;
          if (titleSel.primaryTitle !== "") {
            if (titleSel.fromSuggestion) {
              if (isFirstPage) clearTitleAfterFetch = true;
              const { genre: _g, votes: _v, ...exactFilters } = baseFilters;
              return {
                ...exactFilters,
                searchType: "exact",
                query: titleSel.primaryTitle,
              };
            }
            return {
              ...baseFilters,
              searchType: titleSel.isModified ? "regex" : "exact",
              query: titleSel.primaryTitle,
            };
          } else if (dataQuery.query) {
            return { ...baseFilters, query: dataQuery.query };
          } else {
            return { ...baseFilters };
          }
        } else if (effectiveAutotype === "name") {
          const yearsForName = getNameYears();
          const nameQuery =
            lastNameSelection.id || dataQuery.query || textValue;

          if (lastNameSelection.fromSuggestion) {
            if (isFirstPage) clearNameSuggestionAfterFetch = true;
            const { genre: _g, votes: _v, ...minimalFilters } = rest;
            return {
              ...(yearsForName ? { ...minimalFilters, year: yearsForName } : { ...minimalFilters }),
              query: nameQuery,
            };
          }

          return {
            ...(yearsForName ? { ...rest, year: yearsForName } : { ...rest }),
            query: nameQuery,
          };
        } else {
          return query === "" ? { ...rest } : dataQuery;
        }
      })();

      const headers = { Authorization: `Bearer ${accessToken}` };
      const response = await getData(path, stQuery, headers);
      handleResponse(response);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setIsLoading(false);
      if (clearNameSuggestionAfterFetch) {
        setLastNameSelection((prev) => ({ ...prev, fromSuggestion: false }));
      }
      if (!lastNameSelection.selected) {
        setTextValue("");
      }
      if (clearTitleAfterFetch) {
        updateTitleSelection({ primaryTitle: "", isModified: false, selected: false, fromSuggestion: false });
      }
      if (isFirstPage) {
        setSearching(false)
//        console.log("Stopped searching after initial fetch.");
      }
//      console.log(`Fetch for page ${page} completed. isLoading set to false.`);
    }
  };

  // Pull per-titleType vote ceilings from the backend (precomputed, refreshed
  // out-of-band; cached at module scope). Falls back silently to
  // STATIC_VOTE_MAX_BY_TYPE until live.
  useEffect(() => {
    let active = true;
    fetchVoteMaxByType().then((next) => {
      if (active && next) {
        setVoteMaxByType((prev) => ({ ...prev, ...next }));
      }
    });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    // Set up the resize event listener
    window.addEventListener("resize", handleResize);

    // Mark component as mounted
    isMountedRef.current = true;

    // Cleanup function
    return () => {
      // Reset padding to body

      // Remove the resize event listener
      window.removeEventListener("resize", handleResize);

      // Set isMountedRef to false
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    const historySearch = location.state?.historySearch;
    if (historySearch?.params) {
      applyHistorySearch(historySearch.params);
      navigate(location.pathname, { replace: true, state: {} });
    }
    // applyHistorySearch intentionally omitted to avoid refiring on every render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.state?.historySearch, navigate]);

  /* All useEffects */
  useEffect(() => {
    if (page > 1) {
      // To avoid fetching on initial render
      fetchDataForPage(page);
    }
  }, [page]);

  useEffect(() => {
    if (!hasFetchedInitialData.current && imageDimensions && !showSearch) {
      hasFetchedInitialData.current = true;
  
      const loadInitialData = async () => {
        setSearching(true);
        try {
          setYears(roles["default"].year);
          setLastQuery({
            ...roles["default"],
            titleType: roles["default"].titleType.flatMap((group) => typeGrouping[group]),
            sortType,
            lang: language,
          });
          lastQueryRef.current = {
            ...roles["default"],
            titleType: roles["default"].titleType.flatMap((group) => typeGrouping[group]),
            sortType,
            lang: language,
          };
  
          // Reset posters for a new search
          setPosters([]);
  
          // Trigger data fetch for the first page
          await fetchDataForPage(1, true);
        } catch (error) {
          console.error("Error during initial load: ", error);
        } finally {
          setSearching(false);
        }
      };
  
      loadInitialData();
    }
  }, [imageDimensions, showSearch]);
  

  useEffect(() => {
    lastQueryRef.current = lastQuery;
  }, [lastQuery]);

  useEffect(() => {
    hasMoreDataRef.current = hasMoreData;
  }, [hasMoreData]);

  // After setting 'searching' to true or false

  useEffect(() => {
    isAppendingRef.current = isAppending
//    console.log(`isAppending state changed to: ${isAppending}`);
  }, [isAppending]);  

  const autoTypeOptions = () => {
    return autotypes.map((k, index) => (
      <MenuItem key={index} value={k.value}>
        {k.name}
      </MenuItem>
    ));
  };

  const processData = (data) => {
    if (data.length !== 0) {
//      console.log(`Processing ${data.length} new posters. isAppending: ${isAppendingRef.current}`);
      getPosters(addUrl(renameKey("_id", "id", data)));
    } else {
      if (!isAppendingRef.current) {
//        console.log("No data and not appending. Clearing posters.");
        setPosters([]);
      }
      // Update isAppending if needed
      setIsAppending(false);
      isAppendingRef.current = false
    }
  };

  const getPosters = async (posterData) => {
    const isAppendingCurrent = isAppendingRef.current;
//    console.log(`Appending new posters: ${isAppendingCurrent}`);

    setPosters((prevPosters) => {
      const combinedPosters = isAppendingCurrent
        ? [...prevPosters, ...posterData]
        : posterData;
  
      const uniquePostersMap = new Map();
      combinedPosters.forEach((poster) => {
        uniquePostersMap.set(poster.id, poster);
      });
      const uniquePosters = Array.from(uniquePostersMap.values());
//      console.log(`Total posters after update: ${uniquePosters.length}`);
      return uniquePosters;
    });
  };

  const buildYearArrayFromSelection = ({ birthYear, deathYear }) => {
    const derivedYears = [];

    if (birthYear != null) {
      derivedYears.push(birthYear);
    }

    if (deathYear != null) {
      derivedYears.push(deathYear);
    } else if (birthYear != null) {
      // Actor is still alive, use current year as the upper bound
      derivedYears.push(currentYear);
    }

    return derivedYears;
  };

  const fetchAutoValue = async (val) => {
    if (!tokenPair?.access) {
      try {
        await guestLogin();
      } catch (e) {
        navigate("/login");
        return;
      }
    }

    const basePath = autotype === "title" ? `/autotitle` : `/autoname`;
    const commonParams = { q: val };

    const params =
      autotype === "title"
        ? {
            ...commonParams,
            lang: language,
            isadult: adult ? 1 : 0,
            titletype: toCommaSeparated(lastQueryRef.current.titleType),
          }
        : {
            ...commonParams,
          };

    try {
      const res = await axios.get(`${apiver + basePath}`, {
        params,
        paramsSerializer: (params) => {
          return new URLSearchParams(params).toString().replace(/\+/g, '%20');
        },
      });
      const formatPrimaryName = (item) => {
        const baseName = (item?.primaryName ?? "").split("|")[0];
        const birthYear = item?.birthYear;
        const deathYear = item?.deathYear;

        if (birthYear != null && deathYear != null) {
          return `${baseName} (${birthYear} - ${deathYear})`;
        }

        if (birthYear != null) {
          return `${baseName} (${birthYear} - )`;
        }

        return `${baseName} (unk)`;
      };

      const normalizedResponse = res.data?.map((item) =>
        autotype === "title"
          ? {
              id: item?._id,
              primaryTitle: item?.primaryTitle,
              startYear: item?.startYear,
              rating: item?.rating,
            }
          : {
              id: item?._id,
              primaryName: formatPrimaryName(item),
              baseName: (item?.primaryName ?? "").split("|")[0],
              birthYear: item?.birthYear,
              deathYear: item?.deathYear,
              primaryProfession: item?.primaryProfession,
            }
      ) ?? [];

      if (autotype === "title") {
        setTitleSuggestions(normalizedResponse);
      } else {
        setNameSuggestions(normalizedResponse);
      }
    } catch (err) {}
  };

  const onTitleActorFetchRequested = (value) => {
    fetchAutoValue(escapeRegexCharacters(capitalize(value, true)));
  };

  const titleInputProps = {
    placeholder: `Find ${autotype}`,
    value: textValue,
  };

  const nameInputProps = {
    placeholder: `Find actor`,
    value: textValue,
  };

  const onSelectAdult = (mtPicked) => {
    setAdult(mtPicked);
    setLastQuery((prevState) => ({
      ...prevState,
      isAdult: mtPicked,
    }));
  };

  const onLanguageChange = (selectedLang) => {
    setLanguage(selectedLang);
    setLastQuery((prevState) => ({
      ...prevState,
      lang: selectedLang,
    }));
  };

  const onSelectAutotype = (autoPicked) => {
    const shouldDisable = autoPicked === "name";
    setAutotype(autoPicked);
    setShowDisabled(shouldDisable);
    setPage(1);
    setTextValue("");
    setLastNameSelection({
      id: "",
      primaryName: "",
      baseName: "",
      birthYear: null,
      deathYear: null,
      selected: false,
      fromSuggestion: false,
    });
    updateTitleSelection({ primaryTitle: "", isModified: false, selected: false, fromSuggestion: false });
  };


  const onSelectedTitleType = (selectedGroups) => {
    // Proportionally rescale the votes floor to the new media type's ceiling,
    // preserving the user's relative selectivity, then snap to the new step.
    const oldMax = getGroupVoteMax(selectedTypeGroups, voteMaxByType, typeGrouping);
    const newMax = getGroupVoteMax(selectedGroups, voteMaxByType, typeGrouping);
    const snappedVotes = rescaleVotes(Number(votes) || 0, oldMax, newMax);

    setSelectedTypeGroups(selectedGroups);
    setVotes(snappedVotes);

    // Map the selected groups to the original types
    const selectedTypes = selectedGroups.flatMap((group) => typeGrouping[group]);

    // Update the query state (keep votes in sync with the rescaled floor)
    if (selectedTypes.length > 0) {
      setLastQuery((prevState) => ({
        ...prevState,
        titleType: selectedTypes,
        votes: snappedVotes,
      }));
    } else {
      // Remove titleType from the query if none selected
      setLastQuery((prevState) => {
        const { titleType, ...rest } = prevState;
        return { ...rest, votes: snappedVotes };
      });
    }
  };

  const exitTitleSelectionMode = () => {
    if (lastTitleSelectionRef.current.fromSuggestion) {
      updateTitleSelection({ primaryTitle: "", isModified: false, selected: false, fromSuggestion: false });
    }
  };

  const onSelectedGenre = (genre) => {
    exitTitleSelectionMode();
    setIsAppending(false);
    const newGenres =
      genre[0] === "Genre"
        ? genre.slice(1)
        : genre.length === 0
        ? ["Genre"]
        : genre;
    setSelectedGenres(newGenres);
    if (newGenres[0] !== "Genre") {
      setLastQuery((prevState) => ({
        ...prevState,
        genre: newGenres,
      }));
    } else {
      setLastQuery((prevState) => {
        const { genre, ...rest } = prevState;
        return rest;
      });
    }
  };

  const onSelectYear = (event, yrPicked) => {
    exitTitleSelectionMode();
    setIsAppending(false);
    setYears(yrPicked);
    setLastQuery((prevState) => ({ ...prevState, year: yrPicked }));
  };

  const onChangeRating = (event, ratingPicked) => {
    exitTitleSelectionMode();
    setIsAppending(false);
    setRating(ratingPicked);
  };

  const onChangeVotes = (event, votesPicked) => {
    exitTitleSelectionMode();
    setIsAppending(false);
    setVotes(votesPicked);
    setLastQuery((prevState) => ({
      ...prevState,
      votes: votesPicked,
    }));
  };

  const onSortChange = (newSort) => {
    setSortType(newSort);
    // include it in the lastQuery
    setLastQuery((prev) => ({ ...prev, sortType: newSort }));
    lastQueryRef.current = { ...lastQueryRef.current, sortType: newSort };
  };

  // Modify the onSubmit to set 'searching' to true and reset the fetch state
  const onSubmit = (e, queryOverride, autotypeOverride) => {
    if (e?.preventDefault) {
      e.preventDefault();
    }
    const effectiveYears = queryOverride?.year ?? years;
    const canQuery = effectiveYears.length > 0;
    if (!canQuery) return; 
 
    setSearching(true)
    setPage(1);
    setHasMoreData(true);

    if (queryOverride) {
      // Strip genre so we don't accidentally keep defaults or send empty arrays.
      const { genre: overrideGenre, ...overrideRest } = queryOverride;   

      const currentQuery =  {
        ...overrideRest,
        year: queryOverride.year ?? effectiveYears,
        votes: queryOverride.votes ?? votes,
        titleType: queryOverride.titleType ?? lastQuery.titleType,
        isAdult: queryOverride.isAdult ?? adult,
        sortType: queryOverride.sortType ?? sortType,
        lang: queryOverride.lang ?? language,

        // Only forward genre if the override explicitly includes a non-empty list.
        ...(Array.isArray(overrideGenre) && overrideGenre.length > 0
          ? { genre: overrideGenre }
          : {}),
      };

      setLastQuery(currentQuery);
      lastQueryRef.current = currentQuery;

      // When a new search is submitted, reset the posters
      setPosters([]);

      // Trigger initial fetch
      const resolvedAutotype =
        autotypeOverride ||
        (currentQuery.query && /^nm\d+/i.test(currentQuery.query)
          ? "name"
          : autotype);
      fetchDataForPage(1, true, resolvedAutotype);
      return;
    }
    
    // existing no-override branch stays as-is
    const currentQuery = {
      year: effectiveYears,
      votes,
      titleType: lastQuery.titleType,
      isAdult: adult,
      sortType,
      lang: language,
      ...(selectedGenres[0] !== "Genre" ? { genre: selectedGenres } : {}),
    };

    setLastQuery(currentQuery);
    lastQueryRef.current = currentQuery;
    setPosters([]);

    const resolvedAutotype =
      autotypeOverride ||
      (currentQuery.query && /^nm\d+/i.test(currentQuery.query) ? "name" : autotype);

    fetchDataForPage(1, true, resolvedAutotype);
  };

  const RenderPosters = () => {
    if (posters.length > 0) {
      return (
        <Grid item container direction="row" spacing={3} data-test-id="toprendpost">
          {posters.map((poster) => (
            <Grid
              key={poster.id}
              item
              xs={6}
              sm={4}
              md={3}
              lg={2}
              className={classes.cardroot}
            >
              <Box
                data-test-id="box grid"
                sx={{
                  position: "relative",
                  overflowY: "hidden",
                  width: "100%",
                  paddingBottom: "150%",
                  height: {
                    xs: "200px", // Height for extra-small screens
                    sm: "250px", // Height for small screens
                    md: "300px", // Height for medium screens
                    lg: "300px", // Height for large screens
                  },
                }}
              >
                <Link
                  underline="hover"
                  href={"https://imdb.com/title/" + poster.id}
                  target={"_blank"}
                  rel="noreferrer"
                >
                  <Card
                    raised
                    key={poster.originalTitle}
                    data-test-id="cardraised"
                    style={{ position: "relative" }}
                  >
                    <CardMedia
                      width="100%"
                      component="img"
//                      loading="lazy"
                      sx={{ objectFit: "contain" }}
                      src={`${poster.url}`}
                      alt={poster.originalTitle}
                      className="poster"
                    />
                    <Box
                      sx={{
                        borderRadius: "10px",
                        backgroundColor: "rgba(0, 0, 0, 0.4)",
                        display: "none",
                        flexDirection: "row",
                        justifyContent: "center",
                        alignItems: "center",
                        width: "fit-content",
                        height: "auto",
                        top: 5,
                        right: 5,
                        padding: "4px 8px 4px 8px",
                        position: "absolute",
                        fontSize: "18px",
                        fontWeight: "bold",
                        color: "white",
                      }}
                      className="details"
                    >
                      {poster.rating?.average ?? "N/A"}
                      <StarIcon fontSize="10px" />
                    </Box>
                    <Box
                      sx={{
                        borderRadius: "10px",
                        backgroundColor: "rgba(0, 0, 0, 0.4)",
                        display: "none",
                        flexDirection: "row",
                        justifyContent: "flex-start",
                        alignItems: "center",
                        width: "fit-content",
                        height: "auto",
                        bottom: 3,
                        left: 5,
                        padding: "5px",
                        position: "absolute",
                        fontSize: "18px",
                        fontWeight: "bold",
                        color: "white",
                      }}
                      className="details"
                    >
                      {poster.genres?.[0] ?? poster.genresList?.[0] ?? ""}
                    </Box>
                  </Card>
                </Link>
              </Box>
              <Box>
                <Typography variant="subtitle2">
                  <Link
                    underline="hover"
                    href={"https://imdb.com/title/" + poster.id}
                    target={"_blank"}
                    rel="noreferrer"
                  >
                    {poster.originalTitle.slice(0, 20)}
                  </Link>
                </Typography>
                <Typography
                  variant="subtitle2"
                  sx={{ color: "theme.text.primary" }}
                >
                  {poster.startYear}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      );
    }
  };

  const renderNoData = () => {
    return (
      <Box
        sx={{
          width: "100%",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <SentimentDissatisfiedIcon color="disabled" fontSize="large" />
        <Typography variant="h6" gutterBottom>
          No Results Found
        </Typography>
        <Typography variant="subtitle1">
          Try adjusting your filters or new search.
        </Typography>
      </Box>
    );
  };

  const filtersComponents = [
    <DebouncedSelect
      fetchOptions={onTitleActorFetchRequested}
      autotype={autotype}
      onSelectAutotype={onSelectAutotype}
      autoTypeOptions={autoTypeOptions}
      namesuggestions={nameSuggestions}
      titlesuggestions={titleSuggestions}
      titleInputProps={titleInputProps}
      nameInputProps={nameInputProps}
      setLastTitleSelection={updateTitleSelection}
      setLastNameSelection={setLastNameSelection}
      setIsAppending={setIsAppending}
      setShowDisabled={setShowDisabled}
      lastTitleSelection={lastTitleSelection}
      textValue={textValue}
      setTextValue={setTextValue}
      setLastQuery={setLastQuery}
      onSnapYear={onSnapYear}
    />,
    <MultiSelectType
      selectedTitleType={selectedTypeGroups}
      onSelectedTitleType={onSelectedTitleType}
      movieTypeOptions={movieTypeOptions}
      label="Media Type"
    />,
    <MultiSelectType
      selectedTitleType={selectedGenres}
      onSelectedTitleType={onSelectedGenre}
      movieTypeOptions={genres}
      label="Genre"
      disabled={filtersDisabled}
    />,
    <RatingSlider
      valueText={valuetext}
      curRating={rating}
      ratingChange={onChangeRating}
      disabled={filtersDisabled}
    />,
    <YearSlider
      years={years}
      basicStartYear={basicStartYear}
      currentYear={currentYear}
      onSelectYear={onSelectYear}
      valuetext={valuetext}
      disabled={filtersDisabled}
    />,
    <VoteSlider
      votes={votes}
      valuetext={valuetext}
      onChangeVotes={onChangeVotes}
      disabled={filtersDisabled}
      max={voteMax}
      step={voteStep}
    />,
    <SortSelect              // ← new!
      sortType={sortType}
      onSortChange={onSortChange}
      options={sortOptions}
    />, 
    <AdultToggle adult={adult} onSelectAdult={onSelectAdult} />,
    <ShowFilmsBtn
      onClick={handleShowFilmsClick}
      disabled={showDisabled}
      label={getShowButtonLabel(selectedTypeGroups)}
    />,
  ];

  const languageDropdown = (
    <LanguageSelect language={language} onLanguageChange={onLanguageChange} />
  );

  // For Rendering the Mobile Filters
  const RenderMobileFilters = () => {
    const firstGroup = filtersComponents
      .slice(0, 3)
      .map((Component, index) => (
        <React.Fragment key={index}>{Component}</React.Fragment>
      ));

    const secondGroup = filtersComponents
      .slice(3, 7)
      .map((Component, index) => (
        <React.Fragment key={index + 3}>{Component}</React.Fragment>
      ));

    const thirdGroup = filtersComponents
      .slice(7, 9)
      .map((Component, index) => (
        <React.Fragment key={index + 3}>{Component}</React.Fragment>
      ));

    return (
      <>
        <Grid item xs={6} sm={4}>
          {firstGroup}
        </Grid>
        <Grid item xs={6} sm={4}>
          {secondGroup}
        </Grid>
        <Grid item xs={12} sm={4}>
          {thirdGroup}
        </Grid>
      </>
    );
  };

  // For Rendering the Desktop Filters
  const RenderDesktopFilters = () => {
    const [
      titleDrop,
      movieDrop,
      actionDrop,
      imdbSlider,
      yearsSlider,
      votesSlider,
      sortDropdown,   // ← newly added
      adultCheck,
      submitBtn
    ] = filtersComponents;

    return (
      <Grid
        container
        spacing={1}
        className={classes.filterContainer}
        justifyContent={"space-between"}
      >
        <Grid item xs={3}>
          <Grid container direction="column" spacing={2} paddingBottom={5}>
            <Grid item width={"100%"}>
              {titleDrop}
            </Grid>
            <Grid item width={"100%"}>
              {movieDrop}
            </Grid>
            <Grid item width={"100%"}>
              {actionDrop}
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={3}>
          <Grid container direction="column" spacing={2}>
            <Grid item>{imdbSlider}</Grid>
            <Grid item>{yearsSlider}</Grid>
            <Grid item>{votesSlider}</Grid>
            <Grid item sx={{ pb: 1.5 }}>{sortDropdown}</Grid>
          </Grid>
        </Grid>
        <Grid item xs={3}>
          <Grid container direction="column" spacing={2}>
            <Grid item>{adultCheck}</Grid>
            <Grid item>{languageDropdown}</Grid>
            <Grid item>{submitBtn}</Grid>
          </Grid>
        </Grid>
      </Grid>
    );
  };

  return (
    <>
      {searching && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1000,
          }}
        >
        <Typography variant="h4" color="white">
            Loading...
          </Typography>
          <Circles color="#7D8C91" height={80} width={80} />
        </div>
      )}
      <Container
        maxWidth={false}
        disableGutters
        data-test-id="maxwidth"
        sx={{
          overflow: "hidden",
          marginTop: "62px",
          paddingLeft: "4px",
        }}
      >
        {/* Note: combined view */}
          <Box
            id="scrollableDiv"
            data-test-id="top box"
            sx={{
              overflowY: "auto",
              position: "relative",
              height: "calc(100vh - 62px)",
              width: "100%",
            }}
          >
            {showSearch && (
              <Grid
                data-test-id="popupgrid"
                container
                direction="row"
                justifyContent="center"
                alignItems="center"
                style={{
                  position: "fixed",
                  top: "60px",
                  width: "100%",
                  zIndex: 1000,
                  backgroundColor: "#fff",
                }}
              >
                {width < theme.breakpoints.values.md ? RenderMobileFilters() : RenderDesktopFilters() }
              </Grid>
            )}
            <Box maxWidth="xl">
              <InfiniteScroll
                dataLength={posters.length}
                next={triggerDataFetch}
                hasMore={hasMoreData}
                loader={isLoading ? <CircleLoader /> : null}
                endMessage={
                  !hasMoreData && (
                    <Box sx={{ textAlign: "center", marginTop: "20px" }}>
                      <Typography variant="h6">
                        <b>No more results</b>
                      </Typography>
                      {/* Optional: Add a graphic or icon here */}
                      <SentimentDissatisfiedIcon color="disabled" fontSize="large" />
                    </Box>
                  )
                }
                scrollableTarget="scrollableDiv"
              >
                {posters.length ? (
                  <Grid container direction="row">
                    {RenderPosters()}
                  </Grid>
                ) : (
                  <Grid height="100%" direction="row" container>
                    {renderNoData()}
                  </Grid>
                )}
              </InfiniteScroll>
            </Box>
          </Box>
      </Container>
    </>
  );
};

export default Search;
